import type { StoreApi } from 'zustand'
import type { CancelBookingFormValues } from '../pages/components/CancelBookingAction/types'
import type {
  BookingDetailsData,
  BookingDetailsState,
  BookingDetailsStatePath,
  BookingDetailsStore,
  BookingPreference,
  EditPreferencesFormValues,
  ParticipantEditFormValues,
} from './types'
import type { PropertyIn } from '@/scopes/common/domain/types'
import type { LocationResolver } from '@/scopes/common/infrastructure/dom/location'
import type { QueryStringClient } from '@/scopes/common/infrastructure/query-strings/queryStringClient'
import { formatISO, parseISO } from 'date-fns'
import { ConfigurationClient } from '@/scopes/common/domain/types/configuration'
import configRepository from '@/scopes/common/infrastructure/config/configRepository'
import { createBookingDetailsApiClient } from '../infrastructure/bookingDetailsApiClient'
import { partialCancellationRequestPayloadMapper } from '../infrastructure/mappers/partialCancellationRequestPayloadMapper'
import { participantEditPayloadMapper } from '../infrastructure/mappers/participantEditPayloadMapper'
import { getIsCancelBookingMainBookerSelected } from '../pages/selectors/cancelBookingSelectors'
import { getFullCancellationVariant } from '../pages/selectors/fullCancellationSelectors'
import { sortPreferencesBySelection, sortPreferencesLike } from './editPreferencesRules'
import { BookingNotFoundError } from './errors'
import { getExcursionsStartDate, isEligibleForExcursions } from './excursionRules'
import {
  DEFAULT_FULL_CANCELLATION_COSTS_STATE,
  isFullCancellationCostsDataValid,
} from './fullCancellationCostsRules'
import {
  DEFAULT_PARTIAL_CANCELLATION_FEE_STATE,
  isPartialCancellationFeeDataValid,
  normalizePartialCancellationFeeData,
} from './partialCancellationFeeRules'
import { getContactDomainCode } from './policies/contactDomainCode'
import { isVoucherCancellationUrlValid } from './policies/voucherCancellationUrl'

interface BookingDetailsActionDependencies {
  apiClient: ReturnType<typeof createBookingDetailsApiClient>
  store: StoreApi<BookingDetailsStore>
  queryStringClient: QueryStringClient
  configurationClient: ConfigurationClient
  configurationRepository: Pick<typeof configRepository, 'get'>
  location: LocationResolver
}

export function createBookingDetailsActions({
  apiClient,
  store,
  queryStringClient,
  configurationClient,
  configurationRepository,
  location,
}: BookingDetailsActionDependencies) {
  async function resolveRebookingSearchUrl(bookingDetailsData: BookingDetailsData) {
    const currentSearchUrl = configurationClient.getField('links.search')
    const accommodationUrl = bookingDetailsData.accommodations[0]?.url

    if (bookingDetailsData.rebooking?.state !== 'online' || !accommodationUrl) {
      return currentSearchUrl
    }

    try {
      const configuration = await configurationRepository.get({
        siteName: configurationClient.getField('siteName'),
        url: accommodationUrl,
      })

      return configuration.links.search
    }
    catch (error) {
      console.error('Failed to resolve rebooking search URL:', error)
      return currentSearchUrl
    }
  }

  async function pageLoaded() {
    const state = store.getState()

    if (state.bookingDetailsPageLoadStatus !== 'idle')
      return

    const bookingNumber = queryStringClient.getValue('bookingNumber') || ''

    if (!bookingNumber) {
      state.setIn('bookingDetailsPageLoadStatus', 'error')
      return
    }

    state.setIn('bookingDetailsPageLoadStatus', 'loading')

    const [documentsResult, bookingDetailsResult] = await Promise.allSettled([
      apiClient.getDocumentsData(bookingNumber),
      apiClient.getBookingDetailsData(bookingNumber),
    ] as const)

    if (bookingDetailsResult.status === 'rejected') {
      console.error('Failed to load booking data:', bookingDetailsResult.reason)
      const isNotFound = bookingDetailsResult.reason instanceof BookingNotFoundError
      state.setIn('bookingDetailsPageLoadStatus', isNotFound ? 'not-found' : 'error')
      return
    }

    if (documentsResult.status === 'rejected') {
      console.error('Failed to load booking documents:', documentsResult.reason)
    }

    const bookingDetailsData = bookingDetailsResult.value
    const documentsSettled = documentsResult.status === 'fulfilled'
    const documentsItems = documentsSettled ? documentsResult.value : []
    const documentsLoadStatus = documentsSettled ? 'success' : 'error'
    const eligibleForExcursions = isEligibleForExcursions(
      configurationClient.getField('brandName'),
      bookingDetailsData.isCancelled,
      bookingDetailsData.rawDepartureDate,
      bookingDetailsData.rawReturnDate,
    )
    const rebookingSearchUrl = await resolveRebookingSearchUrl(bookingDetailsData)

    state.initialize({
      bookingNumber: bookingDetailsData.bookingNumber,
      customerId: bookingDetailsData.customerId,
      accommodations: bookingDetailsData.accommodations,
      groupName: bookingDetailsData.groupName,
      groupSize: bookingDetailsData.groupSize,
      destination: bookingDetailsData.destination,
      rawDepartureDate: bookingDetailsData.rawDepartureDate,
      rawReturnDate: bookingDetailsData.rawReturnDate,
      participants: bookingDetailsData.participants,
      bookingParticipantIds: bookingDetailsData.bookingParticipantIds,
      payment: bookingDetailsData.payment,
      activeParticipantId: '',
      isGroupParticipant: bookingDetailsData.isGroupParticipant,
      isGroupResponsible: bookingDetailsData.isGroupResponsible,
      isGroupBooking: bookingDetailsData.isGroupBooking,
      isSelfServiceEnabled: bookingDetailsData.isSelfServiceEnabled,
      areServicesEditable: bookingDetailsData.areServicesEditable,
      isCancelled: bookingDetailsData.isCancelled,
      isAddedByYou: bookingDetailsData.isAddedByYou,
      isTotallySnow: bookingDetailsData.isTotallySnow,
      isPrimavera: bookingDetailsData.isPrimavera,
      bookingDetailsPageLoadStatus: 'success',
      groupSubscriptionModel: bookingDetailsData.groupSubscriptionModel,
      isMasterBooking: bookingDetailsData.isMasterBooking,
      extras: bookingDetailsData.extras,
      specialLuggageExtras: bookingDetailsData.specialLuggageExtras,
      preferences: bookingDetailsData.preferences,
      documents: { items: documentsItems, loadStatus: documentsLoadStatus },
      customerSupportOpeningHours: bookingDetailsData.customerSupportOpeningHours,
      travelGuideContactInfo: bookingDetailsData.travelGuideContactInfo,
      latitude: bookingDetailsData.latitude,
      longitude: bookingDetailsData.longitude,
      rebookedBookingNumber: bookingDetailsData.rebookedBookingNumber,
      excursions: { items: [], loadStatus: eligibleForExcursions && bookingDetailsData.latitude && bookingDetailsData.longitude ? 'loading' : 'idle', allExcursionsLink: '' },
      transfers: {
        type: bookingDetailsData.transferType,
        outbound: null,
        inbound: null,
        loadStatus: bookingDetailsData.transferType !== null ? 'loading' : 'idle',
      },
      deleteBookingRequestStatus: 'idle',
      isDeleteDialogOpen: false,
      isCancelBookingDialogOpen: false,
      canAccountBeDeleted: bookingDetailsData.canAccountBeDeleted,
      specialLuggage: { submitStatus: 'idle', isOpen: false },
      editPreferences: {
        isOpen: false,
        loadStatus: 'idle',
        submitStatus: 'idle',
        wasSubmittedUnchanged: false,
        isStale: false,
        items: [],
      },
      transportType: bookingDetailsData.transportType,
      hasCarRental: bookingDetailsData.hasCarRental,
      busInformation: bookingDetailsData.busInformation,
      flightInformation: bookingDetailsData.flightInformation,
      rebooking: bookingDetailsData.rebooking,
      rebookingSearchUrl,
      partialCancellationFee: DEFAULT_PARTIAL_CANCELLATION_FEE_STATE,
      fullCancellationCosts: DEFAULT_FULL_CANCELLATION_COSTS_STATE,
      cancelBookingSubmitStatus: 'idle',
      cancellationOptions: bookingDetailsData.cancellationOptions,
    })

    const updatedState = store.getState()

    if (bookingDetailsData.transferType !== null) {
      const emptyItem = { pickupLocation: '', pickupTime: '', pickupDate: '', isCancelled: false }
      try {
        const transfersData = await apiClient.getTransfersData(bookingNumber)
        updatedState.setIn('transfers', {
          ...updatedState.transfers,
          type: bookingDetailsData.transferType,
          outbound: bookingDetailsData.hasOutboundTransfer ? (transfersData.outbound ?? emptyItem) : null,
          inbound: bookingDetailsData.hasInboundTransfer ? (transfersData.inbound ?? emptyItem) : null,
          loadStatus: 'success',
        })
      }
      catch (error) {
        console.error('Failed to load transfers:', error)
        updatedState.setIn('transfers', {
          ...updatedState.transfers,
          type: bookingDetailsData.transferType,
          loadStatus: 'error',
        })
      }
    }

    if (eligibleForExcursions && updatedState.latitude && updatedState.longitude) {
      try {
        const startDate = formatISO(parseISO(getExcursionsStartDate(updatedState.rawDepartureDate)), { representation: 'date' })
        const endDate = formatISO(parseISO(updatedState.rawReturnDate), { representation: 'date' })
        const excursionsData = await apiClient.getExcursionsData(
          updatedState.latitude,
          updatedState.longitude,
          startDate,
          endDate,
        )
        updatedState.setIn('excursions', {
          items: excursionsData.excursions ?? [],
          loadStatus: 'success',
          allExcursionsLink: excursionsData.allExcursionsLink,
        })
      }
      catch (e) {
        console.error('Failed to load excursions:', e)
        updatedState.setIn('excursions', {
          ...updatedState.excursions,
          loadStatus: 'error',
        })
      }
    }
  }

  function isDialogLocked() {
    const { deleteBookingRequestStatus } = store.getState()
    return deleteBookingRequestStatus === 'loading' || deleteBookingRequestStatus === 'success'
  }

  function isCancelBookingDialogLocked() {
    const { cancelBookingSubmitStatus } = store.getState()
    return cancelBookingSubmitStatus === 'loading'
  }

  function isCancelBookingSubmitLocked() {
    const { cancelBookingSubmitStatus } = store.getState()
    return cancelBookingSubmitStatus === 'loading' || cancelBookingSubmitStatus === 'success'
  }

  function setInIfCancelBookingDialogStillOpen<Path extends BookingDetailsStatePath>(
    path: Path,
    value: PropertyIn<BookingDetailsState, Path>,
  ) {
    const currentState = store.getState()
    if (!currentState.isCancelBookingDialogOpen)
      return

    currentState.setIn(path, value)
  }

  function resetCancelBookingDialogState(state: ReturnType<typeof store.getState>) {
    state.setIn('partialCancellationFee', DEFAULT_PARTIAL_CANCELLATION_FEE_STATE)
    state.setIn('fullCancellationCosts', DEFAULT_FULL_CANCELLATION_COSTS_STATE)
    state.setIn('cancelBookingSubmitStatus', 'idle')
  }

  function bookingDeletionDialogOpened() {
    store.getState().setIn('isDeleteDialogOpen', true)
  }

  function bookingDeletionDialogClosed() {
    if (isDialogLocked())
      return
    const state = store.getState()
    if (state.deleteBookingRequestStatus === 'error') {
      state.setIn('deleteBookingRequestStatus', 'idle')
    }
    state.setIn('isDeleteDialogOpen', false)
  }

  async function bookingDeletionRequested() {
    const state = store.getState()
    const { customerId, bookingNumber } = state

    if (customerId === null)
      return

    state.setIn('deleteBookingRequestStatus', 'loading')

    try {
      await apiClient.deleteBooking(bookingNumber, customerId)
      state.setIn('deleteBookingRequestStatus', 'success')
    }
    catch {
      state.setIn('deleteBookingRequestStatus', 'error')
    }
  }

  function bookingDeletionAcknowledged() {
    location.redirectTo(configurationClient.getField('links.myPortal'))
  }

  function bookingDeletionErrorDismissed() {
    const state = store.getState()
    state.setIn('deleteBookingRequestStatus', 'idle')
    state.setIn('isDeleteDialogOpen', false)
  }

  async function rebookingButtonClicked() {
    const { bookingNumber } = store.getState()
    await apiClient.updateRebookingNumber(bookingNumber)
  }

  function cancelBookingDialogOpened() {
    store.getState().setIn('isCancelBookingDialogOpen', true)
  }

  function cancelBookingDialogClosed() {
    if (isCancelBookingDialogLocked())
      return

    const state = store.getState()
    state.setIn('isCancelBookingDialogOpen', false)
    resetCancelBookingDialogState(state)
  }

  async function partialCancellationFeeRequested() {
    const state = store.getState()
    const { partialCancellationFee, bookingNumber } = state

    if (partialCancellationFee.status === 'success' || partialCancellationFee.status === 'loading')
      return

    state.setIn('partialCancellationFee', { ...DEFAULT_PARTIAL_CANCELLATION_FEE_STATE, status: 'loading' })

    try {
      const rawData = await apiClient.getPartialCancellationFeeData(bookingNumber)
      const data = normalizePartialCancellationFeeData(rawData)

      if (!isPartialCancellationFeeDataValid(data))
        throw new Error('No partial cancellation fee data')

      setInIfCancelBookingDialogStillOpen('partialCancellationFee', { status: 'success', ...data })
    }
    catch {
      setInIfCancelBookingDialogStillOpen('partialCancellationFee', { ...DEFAULT_PARTIAL_CANCELLATION_FEE_STATE, status: 'error' })
    }
  }

  async function fullCancellationCostsRequested() {
    const state = store.getState()
    const { fullCancellationCosts, bookingNumber } = state

    if (fullCancellationCosts.status === 'success' || fullCancellationCosts.status === 'loading')
      return

    state.setIn('fullCancellationCosts', { ...DEFAULT_FULL_CANCELLATION_COSTS_STATE, status: 'loading' })

    try {
      const data = await apiClient.getFullCancellationCostsData(bookingNumber)

      if (!isFullCancellationCostsDataValid(data))
        throw new Error('No full cancellation costs data')

      setInIfCancelBookingDialogStillOpen('fullCancellationCosts', { status: 'success', ...data })
    }
    catch {
      setInIfCancelBookingDialogStillOpen('fullCancellationCosts', { ...DEFAULT_FULL_CANCELLATION_COSTS_STATE, status: 'error' })
    }
  }

  async function partialCancellationRequested(formValues: CancelBookingFormValues) {
    const state = store.getState()
    const { bookingNumber } = state

    if (isCancelBookingSubmitLocked())
      return

    state.setIn('cancelBookingSubmitStatus', 'loading')

    const isMainBookerCancelled = getIsCancelBookingMainBookerSelected(state, formValues.participantIds)
    const payload = partialCancellationRequestPayloadMapper({
      formValues,
      isMainBookerCancelled,
    })

    try {
      await apiClient.submitPartialCancellationRequest(bookingNumber, payload)

      setInIfCancelBookingDialogStillOpen('cancelBookingSubmitStatus', 'success')
    }
    catch {
      setInIfCancelBookingDialogStillOpen('cancelBookingSubmitStatus', 'error')
    }
  }

  async function cancelBookingConfirmed() {
    const state = store.getState()
    const { bookingNumber } = state
    const variant = getFullCancellationVariant(state)

    if (isCancelBookingSubmitLocked())
      return

    if (variant === null)
      return

    const isWithCost = variant === 'with-cost'

    state.setIn('cancelBookingSubmitStatus', 'loading')

    try {
      await apiClient.submitFullCancellation(bookingNumber, isWithCost)

      setInIfCancelBookingDialogStillOpen('cancelBookingSubmitStatus', 'success')
    }
    catch {
      setInIfCancelBookingDialogStillOpen('cancelBookingSubmitStatus', 'error')
    }
  }

  async function voucherCancellationRequested() {
    const state = store.getState()
    const { cancellationOptions } = state
    const voucherCancellationUrl = cancellationOptions.voucherCancellationUrl

    if (isCancelBookingSubmitLocked())
      return

    if (!isVoucherCancellationUrlValid(voucherCancellationUrl)) {
      setInIfCancelBookingDialogStillOpen('cancelBookingSubmitStatus', 'error')
      return
    }

    state.setIn('cancelBookingSubmitStatus', 'loading')

    try {
      await apiClient.submitVoucherCancellation(voucherCancellationUrl)

      setInIfCancelBookingDialogStillOpen('cancelBookingSubmitStatus', 'success')
    }
    catch {
      setInIfCancelBookingDialogStillOpen('cancelBookingSubmitStatus', 'error')
    }
  }

  async function submitSpecialLuggage(values: Record<string, unknown>) {
    const state = store.getState()
    const { bookingNumber } = state
    state.setIn('specialLuggage.submitStatus', 'loading')
    try {
      await apiClient.submitSpecialLuggage(bookingNumber, values)
      store.getState().setIn('specialLuggage.submitStatus', 'success')
    }
    catch {
      store.getState().setIn('specialLuggage.submitStatus', 'error')
    }
  }

  function setInIfEditPreferencesStillOpen<Path extends BookingDetailsStatePath>(
    path: Path,
    value: PropertyIn<BookingDetailsState, Path>,
  ) {
    const currentState = store.getState()
    if (!currentState.editPreferences.isOpen)
      return

    currentState.setIn(path, value)
  }

  async function editPreferencesOpened() {
    const state = store.getState()
    const { bookingNumber } = state

    state.setIn('editPreferences.isOpen', true)
    state.setIn('editPreferences.submitStatus', 'idle')
    state.setIn('editPreferences.wasSubmittedUnchanged', false)
    state.setIn('editPreferences.loadStatus', 'loading')

    try {
      const preferences = await apiClient.getBookingPreferences(bookingNumber)
      setInIfEditPreferencesStillOpen('editPreferences.items', sortPreferencesBySelection(preferences))
      setInIfEditPreferencesStillOpen('editPreferences.loadStatus', 'success')
    }
    catch {
      setInIfEditPreferencesStillOpen('editPreferences.loadStatus', 'error')
    }
  }

  function editPreferencesClosed() {
    const state = store.getState()

    if (state.editPreferences.submitStatus === 'loading')
      return

    state.setIn('editPreferences.isOpen', false)
    state.setIn('editPreferences.loadStatus', 'idle')
  }

  async function refreshSidebarPreferencesAfterSave() {
    const { bookingNumber } = store.getState()

    try {
      const bookingDetailsData = await apiClient.getBookingDetailsData(bookingNumber)
      store.getState().setIn('preferences', bookingDetailsData.preferences)
    }
    catch (error) {
      console.error('Failed to refresh booking data after saving preferences:', error)
    }
  }

  async function reloadPreferencesAfterSave(submittedPreferences: BookingPreference[]) {
    const { bookingNumber } = store.getState()

    try {
      return {
        preferences: await apiClient.getBookingPreferences(bookingNumber),
        isStale: false,
      }
    }
    catch (error) {
      console.error('Failed to reload booking preferences after saving them:', error)
      return {
        preferences: submittedPreferences,
        isStale: true,
      }
    }
  }

  async function editPreferencesSubmitted(values: EditPreferencesFormValues) {
    const state = store.getState()
    const { bookingNumber, editPreferences: { items, submitStatus } } = state

    if (submitStatus === 'loading')
      return

    state.setIn('editPreferences.submitStatus', 'loading')
    state.setIn('editPreferences.wasSubmittedUnchanged', false)
    state.setIn('editPreferences.isStale', false)

    const selection = items.map(preference => ({
      ...preference,
      ...values.preferences.find(value => value.id === preference.id),
    }))

    try {
      const savedPreferences = await apiClient.setBookingPreferences(bookingNumber, selection)
      const { preferences, isStale } = savedPreferences
        ? { preferences: savedPreferences, isStale: false }
        : await reloadPreferencesAfterSave(selection)
      await refreshSidebarPreferencesAfterSave()
      store.getState().setIn('editPreferences.items', sortPreferencesLike(preferences, items))
      store.getState().setIn('editPreferences.isStale', isStale)
      store.getState().setIn('editPreferences.submitStatus', 'success')
    }
    catch {
      store.getState().setIn('editPreferences.submitStatus', 'error')
    }
  }

  function editPreferencesUnchangedSubmitted() {
    const state = store.getState()

    state.setIn('editPreferences.submitStatus', 'idle')
    state.setIn('editPreferences.wasSubmittedUnchanged', true)
  }

  async function participantEditOpened(participantId: string) {
    const state = store.getState()
    const { participantEdit } = state

    state.setIn('participantEdit.submitStatus', 'idle')
    state.setIn('participantEdit.wasSubmittedUnchanged', false)

    if (participantEdit.participantId !== participantId) {
      state.setIn('participantEdit.externalRequest', { isOpen: false, submitStatus: 'idle', isSent: false, wasSubmittedUnchanged: false })
    }

    const isAlreadyLoadedForParticipant = participantEdit.participantId === participantId
      && participantEdit.loadStatus === 'success'

    if (isAlreadyLoadedForParticipant) {
      state.setIn('participantEdit.isOpen', true)
      return
    }

    const { bookingNumber } = state
    const contactDomainCode = getContactDomainCode(configurationClient.getField('siteName'))
    const participantType = state.participants[participantId]?.type

    state.setIn('participantEdit.participantId', participantId)
    state.setIn('participantEdit.isOpen', true)

    if (!contactDomainCode || !participantType) {
      state.setIn('participantEdit.loadStatus', 'error')
      return
    }

    state.setIn('participantEdit.loadStatus', 'loading')
    try {
      const rules = await apiClient.getParticipantEditRules(contactDomainCode, bookingNumber, participantId, participantType)
      setInIfParticipantEditStillActive(participantId, 'participantEdit.rules', rules)
      setInIfParticipantEditStillActive(participantId, 'participantEdit.loadStatus', 'success')
    }
    catch {
      setInIfParticipantEditStillActive(participantId, 'participantEdit.loadStatus', 'error')
    }
  }

  async function participantEditSaveRequested(values: ParticipantEditFormValues) {
    const state = store.getState()
    const { bookingNumber, participantEdit: { participantId, rules, submitStatus } } = state
    const contactDomainCode = getContactDomainCode(configurationClient.getField('siteName'))

    if (submitStatus === 'loading')
      return

    if (!contactDomainCode) {
      state.setIn('participantEdit.submitStatus', 'error')
      return
    }

    state.setIn('participantEdit.submitStatus', 'loading')

    try {
      const payload = participantEditPayloadMapper(values, rules)
      const updatedParticipantId = await apiClient.submitParticipantEdit(contactDomainCode, bookingNumber, participantId, payload)

      await refreshParticipantsAfterSave(updatedParticipantId ?? participantId)

      setInIfParticipantEditStillActive(participantId, 'participantEdit.submitStatus', 'success')
      setInIfParticipantEditStillActive(participantId, 'participantEdit.loadStatus', 'idle')
      setInIfParticipantEditStillActive(participantId, 'participantEdit.isOpen', false)
    }
    catch {
      setInIfParticipantEditStillActive(participantId, 'participantEdit.submitStatus', 'error')
    }
  }

  async function refreshParticipantsAfterSave(updatedParticipantId: string) {
    const { bookingNumber } = store.getState()

    try {
      const bookingDetailsData = await apiClient.getBookingDetailsData(bookingNumber)
      store.getState().setIn('participants', bookingDetailsData.participants)
      store.getState().changeActiveParticipantId(updatedParticipantId)
    }
    catch (error) {
      console.error('Failed to refresh booking data after saving a participant:', error)
    }
  }

  function setInIfParticipantEditStillActive<Path extends BookingDetailsStatePath>(
    participantId: string,
    path: Path,
    value: PropertyIn<BookingDetailsState, Path>,
  ) {
    const currentState = store.getState()
    const isStillActive = currentState.participantEdit.isOpen
      && currentState.participantEdit.participantId === participantId

    if (!isStillActive)
      return

    currentState.setIn(path, value)
  }

  function participantEditClosed() {
    const state = store.getState()
    state.setIn('participantEdit.isOpen', false)
    state.setIn('participantEdit.externalRequest.isOpen', false)
  }

  function participantEditSaveAcknowledged() {
    store.getState().setIn('participantEdit.submitStatus', 'idle')
  }

  function participantEditExternalRequestOpened() {
    const state = store.getState()
    state.setIn('participantEdit.externalRequest.submitStatus', 'idle')
    state.setIn('participantEdit.externalRequest.wasSubmittedUnchanged', false)
    state.setIn('participantEdit.externalRequest.isOpen', true)
  }

  function participantEditUnchangedSubmitted() {
    store.getState().setIn('participantEdit.wasSubmittedUnchanged', true)
  }

  function participantEditExternalRequestUnchangedSubmitted() {
    store.getState().setIn('participantEdit.externalRequest.wasSubmittedUnchanged', true)
  }

  function participantEditExternalRequestClosed() {
    store.getState().setIn('participantEdit.externalRequest.isOpen', false)
  }

  async function participantEditExternalRequestSubmitted(values: ParticipantEditFormValues) {
    const state = store.getState()
    const { bookingNumber, participantEdit: { participantId, rules, externalRequest } } = state
    const contactDomainCode = getContactDomainCode(configurationClient.getField('siteName'))

    if (externalRequest.submitStatus === 'loading')
      return

    if (!contactDomainCode) {
      state.setIn('participantEdit.externalRequest.submitStatus', 'error')
      return
    }

    state.setIn('participantEdit.externalRequest.submitStatus', 'loading')

    try {
      const payload = participantEditPayloadMapper(values, rules, 'externalRequest')
      await apiClient.submitParticipantEdit(contactDomainCode, bookingNumber, participantId, payload)

      setInIfParticipantEditStillActive(participantId, 'participantEdit.externalRequest.submitStatus', 'success')
      setInIfParticipantEditStillActive(participantId, 'participantEdit.externalRequest.isSent', true)
      setInIfParticipantEditStillActive(participantId, 'participantEdit.externalRequest.isOpen', false)
    }
    catch {
      setInIfParticipantEditStillActive(participantId, 'participantEdit.externalRequest.submitStatus', 'error')
    }
  }

  return {
    pageLoaded,
    submitSpecialLuggage,
    editPreferencesOpened,
    editPreferencesClosed,
    editPreferencesSubmitted,
    editPreferencesUnchangedSubmitted,
    bookingDeletionDialogOpened,
    bookingDeletionDialogClosed,
    bookingDeletionRequested,
    bookingDeletionAcknowledged,
    bookingDeletionErrorDismissed,
    rebookingButtonClicked,
    cancelBookingDialogOpened,
    cancelBookingDialogClosed,
    cancelBookingConfirmed,
    voucherCancellationRequested,
    partialCancellationFeeRequested,
    fullCancellationCostsRequested,
    partialCancellationRequested,
    participantEditOpened,
    participantEditClosed,
    participantEditSaveRequested,
    participantEditSaveAcknowledged,
    participantEditExternalRequestOpened,
    participantEditExternalRequestClosed,
    participantEditExternalRequestSubmitted,
    participantEditUnchangedSubmitted,
    participantEditExternalRequestUnchangedSubmitted,
  }
}

export type BookingDetailsActions = ReturnType<typeof createBookingDetailsActions>
